<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledDropZone from "@/components/StyledDropZone.vue";

import useBillingInformation from "@/composables/useBillingInformation";

import ModalComponent from "@/components/ModalComponent.vue";
import useInternalBillingInformation from "@/internal/composables/useBillingInformation";
import { Hotel } from "@/internal/modules/providers/interfaces/Hotel";
import { useQueryClient } from "@tanstack/vue-query";
import { usePDF, VuePDF } from "@tato30/vue-pdf";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { reactive, ref, watch } from "vue";
import { useToast } from "vue-toastification";
import useHotelBillingInformation from "../composables/useHotelBillingInformation";

interface Props {
  hotel: Hotel;
}

const props = defineProps<Props>();

const toast = useToast();

const queryClient = useQueryClient();
const {
  data: billingInformation,
  isLoading,
  createHotelBillingInformationMutation,
} = useHotelBillingInformation({
  params: {
    id: props.hotel.id,
  },
  immediate: true,
});

const { updateMutation } = useInternalBillingInformation({
  immediate: false,
  params: {
    id: billingInformation.value?.id!,
  },
});

interface Form {
  file: File | null;
}

const form = reactive<Form>({
  file: null,
});

const rules = {
  file: {
    required: helpers.withMessage("El archivo es oblgiatorio ", required),
  },
};

const v$ = useVuelidate(rules, form);

const openModal = ref(false);

const handleOpenModal = () => {
  openModal.value = !openModal.value;
};

const handleSubmit = async () => {
  for (const field of v$.value.$silentErrors) {
    if (Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  await createHotelBillingInformationMutation.mutateAsync({
    params: {
      file: form.file!,
      id: props.hotel.id,
    },
  });

  queryClient.invalidateQueries(["HotelBillingInformation"]);
};

const handleUpdate = async (id: string | number) => {
  for (const field of v$.value.$silentErrors) {
    if (Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  const data = await updateMutation.mutateAsync({
    file: form.file!,
    id,
  });

  if (data) {
    queryClient.invalidateQueries(["HotelBillingInformation"]);

    handleOpenModal();
  }
};

const csf = ref<string | null>();

watch(
  () => billingInformation.value,
  (value) => {
    if (value) {
      csf.value = value.csf;
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

const page = ref(1);
const { pdf, pages } = usePDF(csf);
</script>

<template>
  <RoundedBox class="bg-white">
    <section class="w-full grid gap-[1.563rem]">
      <h2 class="text-xl font-medium">Datos fiscales</h2>

      <section v-if="billingInformation">
        <HasPermission
          :name-permission="'update-billingInformation-internal'"
          :entity-type="'App\\Models\\Hotel'"
        >
          <StyledButton
            label="Actualizar infomacion"
            class="md:ml-auto mr-auto md:mr-0 bg-blue-700 text-white"
            @click="() => handleOpenModal()"
          />
        </HasPermission>

        <div
          class="flex md:flex-row flex-col justify-between border-gray-300 border-b-2 border-spacing-1 mt-5"
        >
          <div>
            <h3 class="text-lg font-medium">Nombre fiscal</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.legalName }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">RFC</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.rfc }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Regime fiscal</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.taxSystemName }}
            </p>
          </div>
        </div>

        <div class="flex md:flex-row flex-col justify-between mt-2">
          <div>
            <h3 class="text-lg font-medium">Cuidad</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.city }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Municipio</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.municipality }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Calle</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.street }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Colonia</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.neighborhood }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Número exterior</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.number }}
            </p>
          </div>
        </div>

        <div class="flex md:flex-row flex-col justify-evenly mt-3">
          <div>
            <h3 class="text-lg font-medium">Codigo Postal</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.zipcode }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Estado</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.state }}
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium">Pais</h3>
            <p class="text-gray-500 text-sm">
              {{ billingInformation?.country }}
            </p>
          </div>
        </div>

        <div class="container-pdf">
          <VuePDF :pdf="pdf" :page="page" />

          <div class="flex gap-2 items-center">
            <StyledButton
              class="bg-[#253C70] text-white"
              @click="page--"
              :disabled="Boolean(pages && page <= 1)"
            >
              <span class="material-symbols-outlined"> chevron_left </span>
            </StyledButton>

            <p>{{ page }} / {{ pages }}</p>

            <StyledButton
              class="bg-[#253C70] text-white"
              @click="page++"
              :disabled="Boolean(pages && page >= pages)"
            >
              <span class="material-symbols-outlined"> chevron_right </span>
            </StyledButton>
          </div>
        </div>
      </section>

      <HasPermission
        :name-permission="'create-billingInformation-internal'"
        :entity-type="'App\\Models\\Hotel'"
      >
        <form
          class="w-full pt-5"
          @submit.prevent="handleSubmit()"
          v-if="!billingInformation"
        >
          <StyledDropZone
            v-model="form.file"
            label="Documento fiscal"
            :accept-types="['application/pdf']"
            class="pb-2"
          />
          <label class="text-gray-500 text-sm">
            Subir el documento origina en pdf
            <span class="text-red-500">*</span>
          </label>

          <div class="flex justify-end">
            <StyledButton
              label="Guardar"
              class="bg-[#253C70] text-white ml-auto"
            />
          </div>
        </form>
      </HasPermission>
    </section>
  </RoundedBox>

  <ModalComponent
    :model-value="openModal"
    @update:model-value="handleOpenModal()"
  >
    <template #modalBody>
      <form
        class="w-full p-5"
        @submit.prevent="handleUpdate(billingInformation?.id!)"
      >
        <StyledDropZone
          v-model="form.file"
          label="Documento fiscal"
          :accept-types="['application/pdf']"
          class="pb-2"
        />

        <label class="text-gray-500 text-sm">
          Subir el documento origina en pdf <span class="text-red-500">*</span>
        </label>

        <StyledButton
          label="Actualizar"
          class="bg-[#253C70] text-white ml-auto"
          :loading="updateMutation.isLoading.value"
        />
      </form>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>

<style scoped>
.container-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
